import React from "react";
import { FormControlLabel, Switch, Checkbox } from "@mui/material";
import type { Job } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { useCheckboxColumnViewModel } from "./checkboxColumn.vm";

export interface CheckboxColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** The field name on the Job object */
	fieldName: keyof Job;
	/** The column identifier */
	columnId: ImportTableColumn;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
	/** Use switch instead of checkbox (more touch-friendly) */
	useSwitch?: boolean;
}

/**
 * Mobile-friendly checkbox/switch component for ImportTable fields.
 * Simplified version of checkboxColumn that doesn't depend on MRT.
 * Uses Checkbox by default for consistency with desktop view.
 */
export const CheckboxColumnMobile: React.FC<CheckboxColumnMobileProps> = ({
	job,
	columnContext,
	fieldName,
	columnId,
	disabled = false,
	label,
	useSwitch = false,
}) => {
	const vm = useCheckboxColumnViewModel(columnContext);
	const currentValue = vm.getCurrentCheckboxValue(job, fieldName);
	const displayLabel = label || columnDisplayNames[columnId];

	// Compute group value for potential group state sync (e.g., isReadyToSample)
	const groupValue = String(job.importGrowerName ?? "");

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		vm.handleIndividualCheckboxChange(job.id, fieldName, event.target.checked, groupValue);
	};

	if (useSwitch) {
		return (
			<FormControlLabel
				control={<Switch checked={currentValue} onChange={handleChange} disabled={disabled} />}
				label={displayLabel}
			/>
		);
	}

	return (
		<FormControlLabel
			control={<Checkbox checked={currentValue} onChange={handleChange} disabled={disabled} />}
			label={displayLabel}
		/>
	);
};
